/**
 * open_file succeeds only after the Files viewer actually opened
 * (embed, inline document error, or file-view). The empty "Select a
 * .kicad_sch…" prompt is not success.
 */

import { fetchRevisionFileOnce, rememberRevisionFilePrefetch } from "./revision-file-fetch.ts";

export const FILES_VIEWER_OPEN_TESTIDS = [
  "kicanvas-document-error",
  "kicanvas-embed",
  "kicanvas-empty-state",
] as const;

export const FILES_VIEWER_EMPTY_TESTID = "kicanvas-select-file";
export const FILES_VIEWER_LOADING_TESTIDS = [
  "hardware-skeleton",
  "files-nextup-skeleton",
  "kicanvas-opening",
] as const;

/** Viewer for `?file=` is in the tree — not a tree-row selector. */
export const FILES_VIEWER_MOUNTED_EVENT = "pcbx:files-viewer-mounted";

/**
 * Kick the revision-file GET and hand the same Promise to inspect.
 * Not fire-and-forget: inspect must consume this request, not start another.
 */
export function prefetchRevisionFile(href: string, fetchImpl: typeof fetch = fetch): void {
  if (!href || typeof fetchImpl !== "function") return;
  const pending = fetchRevisionFileOnce(href, fetchImpl);
  rememberRevisionFilePrefetch(href, pending);
  void pending.catch(() => {
    /* Inspect consumeRevisionFilePrefetch takes the same Promise */
  });
}

export type FilesViewerLocation = {
  search?: string;
  href?: string;
};

export function fileParamFromSearch(search?: string | null, href?: string | null): string | null {
  const query = search && search.startsWith("?") ? search.slice(1) : (search ?? "");
  if (query) {
    const value = new URLSearchParams(query).get("file");
    if (value) return decodeViewerPath(value);
  }
  if (href) {
    try {
      const url = new URL(href, "https://pcbx.ai");
      const value = url.searchParams.get("file");
      if (value) return decodeViewerPath(value);
    } catch {
      /* ignore */
    }
  }
  return null;
}

export function fileParamFromLocation(loc?: FilesViewerLocation | null): string | null {
  const fallback =
    typeof location !== "undefined"
      ? { search: location.search, href: location.href }
      : { search: "", href: "" };
  const search = loc?.search ?? fallback.search;
  const href = loc?.href ?? fallback.href;
  return fileParamFromSearch(search, href);
}

export function fileParamMatchesPath(path: string, loc?: FilesViewerLocation | null): boolean {
  const file = fileParamFromLocation(loc);
  if (!file) return false;
  const want = new Set(viewerPathCandidates(path).map(decodeViewerPath));
  return want.has(decodeViewerPath(file));
}

export function filesViewerEmbedLoaded(doc: Document | null | undefined): boolean {
  if (!doc) return false;
  if (doc.querySelector('[data-kicanvas-loaded="true"]')) return true;
  const embed = doc.querySelector('[data-testid="kicanvas-embed"]') as
    | (HTMLElement & { loaded?: boolean; getAttribute?: (name: string) => string | null })
    | null;
  if (!embed) return false;
  const attr = typeof embed.getAttribute === "function" ? embed.getAttribute("loaded") : null;
  return attr != null || embed.loaded === true;
}

export function filesViewerMounted(doc: Document | null | undefined): boolean {
  if (!doc) return false;
  // kicanvas-opening / ?file= are not mounted. Wait for loaded embed, error, or empty.
  if (filesViewerOpening(doc)) return false;
  return Boolean(
    (doc.querySelector('[data-testid="kicanvas-embed"]') && filesViewerEmbedLoaded(doc)) ||
      doc.querySelector('[data-testid="kicanvas-document-error"]') ||
      doc.querySelector('[data-testid="kicanvas-empty-state"]') ||
      (doc.querySelector('[data-testid="kicanvas-file-view"]') &&
        !filesViewerOpening(doc) &&
        filesViewerEmbedLoaded(doc)),
  );
}

export function announceFilesViewerMounted(path: string, target?: EventTarget | null): void {
  const dest = target ?? (typeof window !== "undefined" ? window : null);
  if (!dest || typeof CustomEvent === "undefined") return;
  dest.dispatchEvent(new CustomEvent(FILES_VIEWER_MOUNTED_EVENT, { detail: { path } }));
}

export function filesViewerOpening(doc: Document | null | undefined): boolean {
  if (!doc) return false;
  const view = doc.querySelector('[data-testid="kicanvas-file-view"]');
  if (!view) return Boolean(doc.querySelector('[data-testid="kicanvas-opening"]'));
  const text = view.textContent ?? "";
  return /Opening…/.test(text) || /Loading this revision/.test(text);
}

export function filesViewerLoading(doc: Document | null | undefined): boolean {
  if (!doc) return true;
  if (FILES_VIEWER_LOADING_TESTIDS.some((id) => doc.querySelector(`[data-testid="${id}"]`))) return true;
  if (filesViewerOpening(doc)) return true;
  return false;
}

export function decodeViewerPath(path: string): string {
  let current = path.trim();
  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      current = next;
    } catch {
      break;
    }
  }
  return current;
}

export function escapeQueryAttr(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function viewerPathCandidates(path: string): string[] {
  const raw = decodeViewerPath(path);
  const encoded = encodeURIComponent(raw);
  return [...new Set([path.trim(), raw, encoded, raw.replace(/ /g, "%20")])];
}

function queryAttr(doc: Document, attrName: string, value: string): Element | null {
  const escaped = escapeQueryAttr(value);
  for (const token of [escaped, value]) {
    try {
      const hit = doc.querySelector(`[${attrName}="${token}"]`);
      if (hit) return hit;
    } catch {
      /* unquoted / invalid CSS identifier — try the next encoding */
    }
  }
  return null;
}

function attrValuesMatch(path: string, value: string): boolean {
  const want = new Set(viewerPathCandidates(path).map(decodeViewerPath));
  return want.has(decodeViewerPath(value));
}

function hasMatchingPathAttr(doc: Document, attrName: string, path: string): boolean {
  for (const candidate of viewerPathCandidates(path)) {
    if (queryAttr(doc, attrName, candidate)) return true;
  }
  if (typeof doc.querySelectorAll !== "function") return false;
  try {
    const nodes = doc.querySelectorAll(`[${attrName}]`);
    for (const node of nodes) {
      const value = node.getAttribute?.(attrName) ?? "";
      if (value && attrValuesMatch(path, value)) return true;
    }
  } catch {
    return false;
  }
  return false;
}

export function filesViewerReadyNode(
  doc: Document | null | undefined,
  path: string,
): boolean {
  if (!doc) return false;
  if (filesViewerOpening(doc)) return false;
  if (doc.querySelector('[data-testid="kicanvas-document-error"]') && hasMatchingPathAttr(doc, "data-kicad-path", path)) {
    return true;
  }
  if (doc.querySelector('[data-testid="kicanvas-empty-state"]')) return true;
  if (
    doc.querySelector('[data-testid="kicanvas-embed"]') &&
    filesViewerEmbedLoaded(doc) &&
    hasMatchingPathAttr(doc, "data-kicad-path", path)
  ) {
    return true;
  }
  if (doc.querySelector('[data-testid="kicanvas-embed"]') && filesViewerEmbedLoaded(doc) && !filesViewerOpening(doc)) {
    return true;
  }
  const view = doc.querySelector('[data-testid="kicanvas-file-view"]');
  if (
    view &&
    filesViewerEmbedLoaded(doc) &&
    !filesViewerOpening(doc) &&
    hasMatchingPathAttr(doc, "data-kicad-path", path)
  ) {
    return true;
  }
  if (hasMatchingPathAttr(doc, "data-kicad-path", path) && !filesViewerOpening(doc) && !filesViewerLoading(doc)) {
    return true;
  }
  return false;
}

export function filesViewerOpened(
  doc: Document | null | undefined,
  path: string,
  loc?: FilesViewerLocation | null,
): boolean {
  if (!doc) return false;
  // ?file= plus Opening… is not ready — wait for the viewer-mounted signal.
  if (filesViewerOpening(doc)) return false;
  if (filesViewerReadyNode(doc, path)) return true;
  if (fileParamMatchesPath(path, loc) && filesViewerReadyNode(doc, path)) return true;
  return false;
}

export type FilesViewerStatusState = "idle" | "loading" | "mounted" | "error";

export type FilesViewerStatus = {
  path: string | null;
  state: FilesViewerStatusState;
};

export function readFilesViewerStatus(
  doc?: Document | null,
  loc?: FilesViewerLocation | null,
): FilesViewerStatus {
  const path = fileParamFromLocation(loc);
  const root = doc ?? (typeof document !== "undefined" ? document : null);
  if (!path) return { path: null, state: "idle" };
  if (!root) return { path, state: "loading" };
  if (root.querySelector('[data-testid="kicanvas-document-error"]')) return { path, state: "error" };
  if (filesViewerReadyNode(root, path)) return { path, state: "mounted" };
  if (filesViewerLoading(root) || filesViewerOpening(root) || fileParamMatchesPath(path, loc)) {
    return { path, state: "loading" };
  }
  return { path, state: "loading" };
}

export function filesViewerStillEmpty(doc: Document | null | undefined): boolean {
  if (!doc) return true;
  if (FILES_VIEWER_OPEN_TESTIDS.some((id) => doc.querySelector(`[data-testid="${id}"]`))) return false;
  return Boolean(doc.querySelector(`[data-testid="${FILES_VIEWER_EMPTY_TESTID}"]`));
}

export async function waitForFilesViewerOpen(
  path: string,
  opts: {
    timeoutMs?: number;
    intervalMs?: number;
    document?: Document | null;
    location?: FilesViewerLocation | null;
    now?: () => number;
    delay?: (ms: number) => Promise<void>;
    signal?: AbortSignal;
    eventTarget?: EventTarget | null;
  } = {},
): Promise<{ ok: true } | { ok: false; reason: string; error?: string }> {
  const timeoutMs = opts.timeoutMs ?? 90_000;
  const intervalMs = opts.intervalMs ?? 50;
  const now = opts.now ?? Date.now;
  const delay = opts.delay ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const started = now();
  let idleSince: number | null = null;
  let mountedPath: string | null = null;
  const target =
    opts.eventTarget ?? (typeof window !== "undefined" ? window : null);
  const onMounted = (event: Event) => {
    const detail = (event as CustomEvent<{ path?: string }>).detail;
    if (typeof detail?.path === "string") mountedPath = detail.path;
  };
  target?.addEventListener?.(FILES_VIEWER_MOUNTED_EVENT, onMounted);
  try {
    while (now() - started <= timeoutMs) {
      if (opts.signal?.aborted) {
        return { ok: false, error: "aborted", reason: "Stopped" };
      }
      const doc = opts.document ?? (typeof document !== "undefined" ? document : null);
      const loc = opts.location;
      if (filesViewerOpened(doc, path, loc)) return { ok: true };
      if (mountedPath && attrValuesMatch(path, mountedPath) && !filesViewerOpening(doc)) return { ok: true };
      const waitingForMount =
        filesViewerLoading(doc) ||
        filesViewerOpening(doc) ||
        filesViewerStillEmpty(doc) ||
        (fileParamMatchesPath(path, loc) && !filesViewerMounted(doc));
      if (waitingForMount) {
        idleSince = null;
        await delay(intervalMs);
        continue;
      }
      if (idleSince == null) idleSince = now();
      if (now() - idleSince > 8_000) break;
      await delay(intervalMs);
    }
  } finally {
    target?.removeEventListener?.(FILES_VIEWER_MOUNTED_EVENT, onMounted);
  }
  const doc = opts.document ?? (typeof document !== "undefined" ? document : null);
  if (filesViewerOpened(doc, path, opts.location)) return { ok: true };
  if (filesViewerLoading(doc) && !fileParamMatchesPath(path, opts.location)) {
    return {
      ok: false,
      error: "viewer_still_loading",
      reason: `Files viewer is still opening "${path}".`,
    };
  }
  if (filesViewerStillEmpty(doc)) {
    return { ok: false, error: "viewer_not_opened", reason: `Files viewer did not open "${path}".` };
  }
  return { ok: false, error: "viewer_not_opened", reason: `No Files viewer for "${path}".` };
}

export function waitForKiCanvasEmbed(
  embed: HTMLElement,
  opts: { timeoutMs?: number } = {},
): Promise<void> {
  const timeoutMs = opts.timeoutMs ?? 90_000;
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (err?: Error) => {
      if (settled) return;
      settled = true;
      embed.removeEventListener("error", onError);
      embed.removeEventListener("kicanvas:error", onError);
      clearInterval(poll);
      clearTimeout(timer);
      if (err) reject(err);
      else resolve();
    };
    const onError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      const message =
        detail?.message ||
        (event as ErrorEvent).message ||
        "KiCanvas failed to load this document.";
      finish(new Error(message));
    };
    const loaded = () => {
      const node = embed as HTMLElement & { loaded?: boolean; project?: { loaded?: boolean } };
      return (
        embed.hasAttribute("loaded") ||
        node.loaded === true ||
        node.project?.loaded === true
      );
    };
    embed.addEventListener("error", onError);
    embed.addEventListener("kicanvas:error", onError);
    const poll = setInterval(() => {
      if (loaded()) finish();
    }, 40);
    const timer = setTimeout(() => {
      if (loaded()) finish();
      else finish(new Error("KiCanvas embed did not load"));
    }, timeoutMs);
    if (loaded()) finish();
  });
}
